import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Activity, RefreshCw, CheckCircle2, AlertTriangle, XCircle, Database, Package, Building2, FileClock } from "lucide-react";

// Colours per spec: pasture-green / amber / alert-red
const STATUS_META = {
  ok:       { label: "Healthy",  color: "#4A7C59", bg: "rgba(74,124,89,0.14)",  Icon: CheckCircle2 },
  degraded: { label: "Degraded", color: "#E8A838", bg: "rgba(232,168,56,0.16)", Icon: AlertTriangle },
  error:    { label: "Error",    color: "#C0392B", bg: "rgba(192,57,43,0.14)",  Icon: XCircle },
};

const fmtRelative = (isoTs) => {
  if (!isoTs) return "—";
  const diff = Math.max(0, Math.floor((Date.now() - new Date(isoTs).getTime()) / 1000));
  if (diff < 5) return "just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
};

const fmtAbs = (isoTs) => {
  if (!isoTs) return "—";
  try {
    return new Date(isoTs).toLocaleString(undefined, {
      day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return isoTs; }
};

export default function SystemHealthWidget() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [checkedAt, setCheckedAt] = useState(null);
  const [, forceTick] = useState(0);

  const load = useCallback(async () => {
    setBusy(true); setError("");
    try {
      // Bypass axios' baseURL error handling for 503 (endpoint still returns JSON body)
      const res = await api.get("/health", { validateStatus: (s) => s === 200 || s === 503 });
      setData(res.data);
      setCheckedAt(Date.now());
    } catch (e) {
      setError(formatApiError(e));
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Rerender every 15s so the "Last checked: X seconds ago" label stays fresh
  useEffect(() => {
    const t = setInterval(() => forceTick((n) => n + 1), 15000);
    return () => clearInterval(t);
  }, []);

  const status = data?.status || (error ? "error" : "ok");
  const meta = STATUS_META[status] || STATUS_META.ok;
  const StatusIcon = meta.Icon;

  return (
    <section
      className="se-card mt-6"
      data-testid="system-health-widget"
      style={{ borderTop: `2px solid ${meta.color}` }}
    >
      <header className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="se-label flex items-center gap-2">
            <Activity size={12} /> Platform health
          </div>
          <h3 className="font-heading mt-1" style={{ fontSize: 22 }}>System Health</h3>
        </div>
        <button
          className="se-btn se-btn-ghost"
          onClick={load}
          disabled={busy}
          data-testid="system-health-refresh"
          style={{ padding: "6px 12px" }}
        >
          <RefreshCw size={14} className={busy ? "spinning" : ""} /> {busy ? "Checking…" : "Refresh"}
        </button>
      </header>

      {/* Overall status pill */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full"
          style={{ background: meta.bg, color: meta.color, fontWeight: 600 }}
          data-testid="system-health-status-badge"
        >
          <StatusIcon size={16} /> {meta.label}
        </div>
        <span className="text-xs" style={{ color: "#8C9E8A" }} data-testid="system-health-last-checked">
          Last checked: {checkedAt ? fmtRelative(new Date(checkedAt).toISOString()) : "—"}
        </span>
      </div>

      {error && !data && (
        <div className="text-sm px-3 py-2 rounded-lg mb-3" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid="system-health-error">
          {error}
        </div>
      )}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="system-health-grid">
          <MetricRow
            icon={<Database size={14} />}
            label="MongoDB"
            testid="system-health-mongodb"
            value={
              <span className="inline-flex items-center gap-2">
                <StatusDot color={data.checks?.mongodb?.status === "ok" ? "#4A7C59" : "#C0392B"} />
                <span style={{ fontWeight: 600, color: data.checks?.mongodb?.status === "ok" ? "#3B2F1E" : "#922B21" }}>
                  {data.checks?.mongodb?.status === "ok" ? "Online" : "Offline"}
                </span>
                <span className="text-xs" style={{ color: "#8C9E8A", fontVariantNumeric: "tabular-nums" }}>
                  {data.checks?.mongodb?.response_time_ms}ms
                </span>
              </span>
            }
          />
          <MetricRow
            icon={<Package size={14} />}
            label="App version"
            testid="system-health-version"
            value={<span style={{ fontVariantNumeric: "tabular-nums" }}>{data.version || "unknown"}</span>}
          />
          <MetricRow
            icon={<Building2 size={14} />}
            label="Active tenants"
            testid="system-health-tenants"
            value={<span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{data.platform?.tenant_count ?? "—"}</span>}
          />
          <MetricRow
            icon={<FileClock size={14} />}
            label="Last migration"
            testid="system-health-migration"
            value={
              data.platform?.last_migration?.name ? (
                <span title={fmtAbs(data.platform.last_migration.applied_at)}>
                  <span style={{ fontWeight: 600 }}>{data.platform.last_migration.name}</span>
                  <span className="text-xs ml-2" style={{ color: "#8C9E8A" }}>
                    {fmtRelative(data.platform.last_migration.applied_at)}
                  </span>
                </span>
              ) : (
                <span className="text-xs" style={{ color: "#8C9E8A" }}>None tracked</span>
              )
            }
          />
        </div>
      )}

      {data?.checks?.mongodb?.detail && (
        <div className="mt-3 text-xs px-3 py-2 rounded" style={{ background: "rgba(192,57,43,0.08)", color: "#922B21" }} data-testid="system-health-mongodb-detail">
          MongoDB error: {data.checks.mongodb.detail}
        </div>
      )}

      <style>{`
        .spinning { animation: se-health-spin 1s linear infinite; }
        @keyframes se-health-spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>
    </section>
  );
}

function MetricRow({ icon, label, value, testid }) {
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-lg"
      style={{ background: "rgba(212,169,106,0.06)", border: "0.5px solid rgba(212,169,106,0.2)" }}
      data-testid={testid}
    >
      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "rgba(124,92,58,0.12)", color: "#7C5C3A" }}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs" style={{ color: "#8C9E8A", textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
        <div className="text-sm mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function StatusDot({ color }) {
  return (
    <span
      style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: color, boxShadow: `0 0 0 3px ${color}22` }}
      aria-hidden
    />
  );
}
